import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import client from "@/lib/api";
import { useNavigate } from "@/lib/useNav";
import { cn } from "@/lib/utils";
import { useBlockData, BlockEmpty } from "@/components/dashboard/useBlockData";
import { fmtPct, fmtMoney, fmtMoney0 } from "@/lib/stats";
import { ChartTip, changeTone, paceColor } from "@/components/shared/ChartTip";

const KEYS = [["rev", "Revenue"], ["occ", "Occupancy"], ["adr", "ADR"], ["revpar", "RevPAR"]];
const diff = (k, t) => (!t ? null : k === "occ" ? t[0] - t[1] : t[1] ? t[0] / t[1] - 1 : null);
const fmt = (k, d) => (d == null ? "—" : k === "occ" ? `${d >= 0 ? "+" : ""}${(d * 100).toFixed(1)} pts` : `${d >= 0 ? "+" : ""}${(d * 100).toFixed(1)}%`);
const color = (t) => paceColor(t?.[0], t?.[1], "#e2e8f0");
const actual = { rev: fmtMoney0, occ: fmtPct, adr: fmtMoney, revpar: fmtMoney };
const monLbl = (ym, y) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-AU", { month: "short", ...(y ? { year: "numeric" } : {}) });
const addMon = (ym, n) => { const d = new Date(`${ym}-01T00:00:00`); d.setMonth(d.getMonth() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
// Months shown, based on the latest upload across properties
const paceRange = (rows, period) => {
  const ym = rows.map((r) => r.report_date || "").sort().pop()?.slice(0, 7);
  if (!ym) return period === "month" ? "This month" : "Next 12 months";
  return period === "month" ? `Showing ${monLbl(ym, true)}` : `Showing ${monLbl(ym, true)} – ${monLbl(addMon(ym, 11), true)}`;
};


function PaceTip({ active, payload, k }) {
  const p = active && payload?.[0]?.payload;
  if (!p) return null;
  const label = KEYS.find(([key]) => key === k)[1];
  const tone = changeTone(p.t?.[0], p.t?.[1]);
  return (
    <ChartTip testid="pace-block-tooltip" title={p.full} tag={label}
      rows={[{ label: "This year", color: "#0284c7", value: actual[k](p.t?.[0]) }, { label: "Same time LY", color: "#cbd5e1", value: actual[k](p.t?.[1]) }]}
      change={{ label: "vs same time LY", text: p.d == null ? null : fmt(k, p.d), tone }} />
  );
}

function Cells({ totals, onClick, bold, testid }) {
  return KEYS.map(([k]) => {
    const d = diff(k, totals?.[k]);
    return <td key={k} onClick={onClick} className={cn("px-2 py-0.5 text-right tabular-nums", bold && "font-bold")} style={{ color: paceColor(totals?.[k]?.[0], totals?.[k]?.[1], "#cbd5e1") }} data-testid={testid && `${testid}-${k}`}>{fmt(k, d)}</td>;
  });
}

export function PacePropertiesBlock() {
  const navigate = useNavigate();
  const [k, setK] = useState("rev");
  const [period, setPeriod] = useState("month");
  const [data] = useBlockData(() => client.get("/stats/pace-overview", { params: { period } }).then((r) => r.data), [period]);
  if (!data) return null;
  if (!data.rows?.length) return <BlockEmpty>No stats uploaded yet</BlockEmpty>;
  const open = (pid) => navigate(`/stats?tab=pace&property=${pid}&view=summary`);
  const chart = data.rows.map((r) => ({ id: r.property_id, full: r.property_name, name: r.property_name.length > 26 ? `${r.property_name.slice(0, 25)}…` : r.property_name, t: r.totals[k], d: diff(k, r.totals[k]) })).sort((a, b) => (b.d ?? -9) - (a.d ?? -9));
  const byId = Object.fromEntries(chart.map((c) => [c.id, c]));
  return (
    <div className="space-y-2" data-testid="pace-block">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="pace-block-period">
            {[["month", "This Month"], ["year", "This Year"]].map(([key, label]) => (
              <button key={key} onClick={() => setPeriod(key)} data-testid={`pace-block-period-${key}`}
                className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", period === key ? "bg-white text-sky-700 shadow-sm" : "text-slate-500 hover:text-slate-800")}>{label}</button>
            ))}
          </div>
          <p className="text-xs text-slate-400" data-testid="pace-block-range">{paceRange(data.rows, period)} vs same time LY</p>
        </div>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="pace-block-metric">
          {KEYS.map(([key, label]) => (
            <button key={key} onClick={() => setK(key)} data-testid={`pace-block-metric-${key}`}
              className={cn("rounded-md px-2.5 py-1 text-xs font-semibold transition-colors", k === key ? "bg-white text-sky-700 shadow-sm" : "text-slate-500 hover:text-slate-800")}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ height: Math.max(100, chart.length * 20 + 16) }} data-testid="pace-block-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} layout="vertical" margin={{ left: 4, right: 4, top: 2, bottom: 2 }}>
            <XAxis type="number" hide domain={[(m) => Math.min(0, m), (m) => Math.max(0, m)]} />
            <YAxis yAxisId="n" type="category" dataKey="id" tickFormatter={(id) => byId[id]?.name} width={190} interval={0} tick={{ fontSize: 10, fill: "#475569" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="v" orientation="right" type="category" dataKey="id" tickFormatter={(id) => fmt(k, byId[id]?.d)} width={70} interval={0} tick={{ fontSize: 10, fill: "#334155", fontWeight: 600 }} axisLine={false} tickLine={false} />
            <ReferenceLine yAxisId="n" x={0} stroke="#94a3b8" />
            <Tooltip shared={false} content={<PaceTip k={k} />} cursor={false} />
            <Bar yAxisId="n" dataKey="d" radius={3} barSize={12} onClick={(e) => open(e.id)} className="cursor-pointer">
              {chart.map((c) => <Cell key={c.id} fill={color(c.t)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[440px] text-xs" data-testid="pace-block-table">
          <thead><tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wide text-slate-400">
            <th className="px-2 py-0.5">Property</th>{KEYS.map(([key, l]) => <th key={key} className="px-2 py-0.5 text-right">{l}</th>)}
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.property_id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid={`pace-block-row-${r.property_id}`}>
                <td onClick={() => open(r.property_id)} className="px-2 py-0.5 font-medium text-slate-700">{r.property_name}</td>
                <Cells totals={r.totals} onClick={() => open(r.property_id)} testid={`pace-block-cell-${r.property_id}`} />
              </tr>
            ))}
            <tr className="bg-slate-50" data-testid="pace-block-group">
              <td className="px-2 py-0.5 font-bold text-slate-800">All properties</td>
              <Cells totals={data.group} bold testid="pace-block-group" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
